import React from "react";
// ============================================================
// Broker Signature — the EveryPlace Designated Broker (Amber
// Valentine) adopts her electronic signature ONCE. It is stored
// locally and embedded into every form where she is a signer.
// Shared by Agent Onboarding.html (renders her mark in forms)
// and Envelopes.html (adopt / manage session, counter-signing).
// ============================================================
const { useState: bs_uS, useEffect: bs_uE, useRef: bs_uR } = React;

const BROKER_SIG_KEY = "ep_broker_sig_v1";

// Synthetic but realistic provenance for the adoption session.
function _coarseIP() {
  // stable per-browser so the audit trail is internally consistent
  let ip = localStorage.getItem("ep_session_ip");
  if (!ip) {
    ip = `73.162.${40 + Math.floor(Math.random() * 80)}.${10 + Math.floor(Math.random() * 200)}`;
    localStorage.setItem("ep_session_ip", ip);
  }
  return ip;
}
function _fmtStamp(d) {
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
}

function getBrokerSig() {
  try { return JSON.parse(localStorage.getItem(BROKER_SIG_KEY)); }
  catch { return null; }
}
function setBrokerSigStore(obj) {
  localStorage.setItem(BROKER_SIG_KEY, JSON.stringify(obj));
  window.dispatchEvent(new Event("ep-broker-sig"));
}
function clearBrokerSig() {
  localStorage.removeItem(BROKER_SIG_KEY);
  window.dispatchEvent(new Event("ep-broker-sig"));
}
// React hook so any component re-renders when the broker adopts / clears.
function useBrokerSig() {
  const [sig, setSig] = bs_uS(getBrokerSig());
  bs_uE(() => {
    const h = () => setSig(getBrokerSig());
    window.addEventListener("ep-broker-sig", h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener("ep-broker-sig", h); window.removeEventListener("storage", h); };
  }, []);
  return sig;
}

const BROKER = {
  name: "Amber Valentine",
  title: "Designated Broker · EveryPlace",
  email: "amber.valentine@everyplace.com",
  license: "BR642301000",
};

// ---------- The visual mark, embedded onto a form line ----------
// Renders the adopted signature sitting on a signature rule, with the
// printed name + a tamper-evident verification caption beneath.
function BrokerSignatureMark({ sig, label, compact }) {
  if (!sig) return null;
  return (
    <div style={{ position: "relative" }}>
      <div style={{
        position: "relative", height: compact ? 40 : 48, display: "flex",
        alignItems: "flex-end", borderBottom: "1px solid #1a1a1a",
      }}>
        {sig.type === "drawn" ? (
          <img src={sig.data} alt={`${sig.name} signature`}
            style={{ height: compact ? 38 : 46, marginBottom: 1, mixBlendMode: "multiply" }}/>
        ) : (
          <span style={{
            font: `400 ${compact ? 34 : 44}px/1 'Alex Brush', cursive`,
            color: "#1a2c66", paddingLeft: 4, marginBottom: 2, whiteSpace: "nowrap",
          }}>{sig.data}</span>
        )}
        {/* DS-style adopted stamp */}
        <span style={{
          position: "absolute", right: 0, bottom: 3, fontSize: 7.5, lineHeight: 1.25,
          fontFamily: "Consolas, monospace", color: "#7a7363", textAlign: "right",
          letterSpacing: "0.02em",
        }}>
          <span style={{ color: "#1f8a5b", fontWeight: 700 }}>✓ EveryPlace e-Sign</span><br/>
          ID {sig.sigId}
        </span>
      </div>
      <div style={{ fontSize: 10, marginTop: 4, color: "#555", display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span>{label || `${sig.name} — Designated Broker`}</span>
        <span style={{ fontFamily: "Consolas, monospace", color: "#7a7363" }}>{sig.signedShort || sig.adoptedShort}</span>
      </div>
    </div>
  );
}

// ---------- Self-contained drawing pad ----------
function BrokerSigPad({ canvasRef, onDrawn }) {
  bs_uE(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#1a2c66"; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
    let drawing = false;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0];
      return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
    };
    const down = (e) => { drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); onDrawn && onDrawn(); };
    const move = (e) => { if (!drawing) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); e.preventDefault && e.preventDefault(); };
    const up = () => { drawing = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", up);
    };
  }, []);
  return <canvas ref={canvasRef} width={460} height={130} className="sig-pad" style={{ display: "block", width: "100%" }}/>;
}

// ---------- One-time adoption session ----------
// mode: "adopt" (no sig yet) | "replace" (re-adopt)
function BrokerSigAdoptModal({ onClose, onAdopted }) {
  const existing = getBrokerSig();
  const [method, setMethod] = bs_uS("type"); // type | draw
  const [name, setName] = bs_uS(existing?.name || BROKER.name);
  const [initials, setInitials] = bs_uS(existing?.initials || "AV");
  const [agreed, setAgreed] = bs_uS(false);
  const [drawn, setDrawn] = bs_uS(false);
  const canvasRef = bs_uR(null);

  function adopt() {
    const now = new Date();
    const sigId = "BSIG-" + Math.random().toString(36).slice(2, 8).toUpperCase() + "-" + now.getFullYear();
    const base = {
      name: name.trim(), initials: initials.trim().toUpperCase(),
      title: BROKER.title, email: BROKER.email, license: BROKER.license,
      sigId,
      adoptedAt: now.toISOString(),
      adoptedStamp: _fmtStamp(now),
      adoptedShort: now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      ip: _coarseIP(),
      ua: "Chrome 126 · macOS",
      consent: "ESIGN/UETA electronic-records consent accepted",
    };
    if (method === "draw") {
      const c = canvasRef.current;
      base.type = "drawn";
      if (c) {
        // Scale down to a small fixed size before encoding — keeps the base64
        // payload under BoldSign's field pre-fill size limit.
        const out = document.createElement("canvas");
        out.width = 300; out.height = 85;
        out.getContext("2d").drawImage(c, 0, 0, 300, 85);
        base.data = out.toDataURL("image/png");
      } else {
        base.data = "";
      }
    } else {
      base.type = "typed";
      base.data = name.trim();
    }
    setBrokerSigStore(base);
    onAdopted && onAdopted(base);
  }

  const canAdopt = agreed && (method === "draw" ? drawn : name.trim().length > 1);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onClose()}>
      <div style={{ width: "min(620px, 96vw)", background: "var(--ep-bg-elevated)", borderRadius: 4, overflow: "hidden", animation: "slideUp 240ms var(--ep-ease)" }}>
        {/* header */}
        <div style={{ background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)", padding: "20px 26px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="ep-caption" style={{ color: "rgba(255,245,228,0.7)" }}>{existing ? "Replace broker signature" : "One-time setup"}</div>
            <div style={{ font: "300 24px/1.1 var(--ep-font-display)", marginTop: 4 }}>Adopt your broker signature</div>
          </div>
          <button className="ep-btn ghost sm" onClick={onClose} style={{ color: "var(--ep-saguaro-blossom)" }}>Close</button>
        </div>

        <div style={{ padding: 26 }}>
          <p style={{ fontSize: 13.5, color: "var(--ep-fg-muted)", margin: "0 0 18px", maxWidth: "62ch" }}>
            This is stored once for <strong style={{ color: "var(--ep-origin)" }}>{BROKER.name}</strong>, {BROKER.title}. EveryPlace then
            applies it automatically wherever you are the designated-broker signer — agent ICAs, association change forms, and listing/escrow
            transfers — so you never re-sign each envelope by hand.
          </p>

          {/* method toggle */}
          <div style={{ display: "flex", gap: 0, border: "1px solid var(--ep-border)", borderRadius: 2, overflow: "hidden", width: "fit-content", marginBottom: 18 }}>
            {[["type", "Type"], ["draw", "Draw"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setMethod(k)} style={{
                padding: "8px 22px", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                border: 0, cursor: "pointer",
                background: method === k ? "var(--ep-origin)" : "transparent",
                color: method === k ? "var(--ep-saguaro-blossom)" : "var(--ep-fg-muted)",
              }}>{lbl}</button>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: method === "draw" ? "1fr" : "2fr 1fr", gap: 18 }}>
            {method === "draw" ? (
              <div>
                <div className="ep-eyebrow">Draw your signature</div>
                <BrokerSigPad canvasRef={canvasRef} onDrawn={() => setDrawn(true)}/>
                <button className="ep-link" style={{ fontSize: 12, marginTop: 6, background: "transparent", border: 0, cursor: "pointer", color: "var(--ep-sunset)" }}
                  onClick={() => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); setDrawn(false); }}>
                  Clear
                </button>
              </div>
            ) : (
              <label className="ep-field">
                <span className="ep-eyebrow">Full legal name</span>
                <input className="ep-input" value={name} onChange={(e) => setName(e.target.value)}/>
                <div className="sig-pad typed" style={{ marginTop: 10, color: "#1a2c66" }}>{name}</div>
              </label>
            )}
            {method !== "draw" && (
              <label className="ep-field">
                <span className="ep-eyebrow">Initials</span>
                <input className="ep-input" value={initials} onChange={(e) => setInitials(e.target.value.slice(0, 4).toUpperCase())}/>
                <div className="sig-pad typed" style={{ marginTop: 10, textAlign: "center", fontSize: 34, color: "#1a2c66" }}>{initials}</div>
              </label>
            )}
          </div>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 20, fontSize: 12.5, color: "var(--ep-fg-muted)", cursor: "pointer", lineHeight: 1.5 }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ accentColor: "var(--ep-sunset)", width: 15, height: 15, marginTop: 2 }}/>
            <span>I consent to use an electronic signature under the U.S. ESIGN Act &amp; Arizona UETA. I agree this adopted mark is the legally binding
              representation of my signature, applied on my authority as Designated Broker, until I replace or revoke it here.</span>
          </label>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 22, paddingTop: 18, borderTop: "1px solid var(--ep-border)" }}>
            <div style={{ fontSize: 11, color: "var(--ep-fg-subtle)", fontFamily: "Consolas, monospace" }}>
              Session {_coarseIP()} · {BROKER.email}
            </div>
            <button className="ep-btn primary" disabled={!canAdopt} onClick={adopt}>
              Adopt &amp; store signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export {
  getBrokerSig, setBrokerSigStore, clearBrokerSig, useBrokerSig,
  BrokerSignatureMark, BrokerSigAdoptModal, BrokerSigPad, BROKER,
};
